import { createSlice } from "@reduxjs/toolkit";
import {
  getDashboardStats,
  getRecentActivities,
  getSalesChartData,
  getCoursePerformance,
  getRevenueAnalytics,
  getAllUsers,
  getUserById,
  updateUser,
  deleteUser,
  getAllCourses,
  getCourseById,
  createCourse,
  updateCourse,
  deleteCourse,
  getAllOrders,
  getOrderById,
  updateOrderStatus,
  deleteOrder,
  getAllPromoCodes,
  createPromoCode,
  deletePromoCode,
} from "./asyncThunks";

const initialState = {
  // Dashboard data
  stats: null,
  activities: null,
  salesChartData: null,
  coursePerformance: null,
  revenueAnalytics: null,

  // User management data
  users: [],
  selectedUser: null,
  usersPagination: null,

  // Course management data
  courses: [],
  selectedCourse: null,
  coursesPagination: null,
  coursesTotals: null,

  // Order management data
  orders: [],
  selectedOrder: null,
  ordersPagination: null,

  // Promo code management data
  promoCodes: [],
  promoCodesPagination: null,

  // Loading states
  statsLoading: false,
  activitiesLoading: false,
  salesChartDataLoading: false,
  coursePerformanceLoading: false,
  revenueAnalyticsLoading: false,
  usersLoading: false,
  userLoading: false,
  userUpdateLoading: false,
  userDeleteLoading: false,
  coursesLoading: false,
  courseLoading: false,
  courseCreateLoading: false,
  courseUpdateLoading: false,
  courseDeleteLoading: false,
  ordersLoading: false,
  orderLoading: false,
  orderUpdateLoading: false,
  orderDeleteLoading: false,
  promoCodesLoading: false,
  promoCodeCreateLoading: false,
  promoCodeDeleteLoading: false,

  // Error states
  error: null,

  // UI state
  selectedTimeframe: "7days",
  selectedPeriod: "monthly",
  userFilters: {
    search: "",
    page: 1,
    limit: 10,
  },
  courseFilters: {
    search: "",
    page: 1,
    limit: 10,
  },
  orderFilters: {
    search: "",
    status: "completed",
    dateFrom: "",
    dateTo: "",
    page: 1,
    limit: 10,
  },
  promoCodeFilters: {
    search: "",
    page: 1,
    limit: 10,
  },
};

export const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setTimeframe: (state, action) => {
      state.selectedTimeframe = action.payload;
    },
    setPeriod: (state, action) => {
      state.selectedPeriod = action.payload;
    },
    setUserFilters: (state, action) => {
      state.userFilters = { ...state.userFilters, ...action.payload };
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
    setCourseFilters: (state, action) => {
      state.courseFilters = { ...state.courseFilters, ...action.payload };
    },
    clearSelectedCourse: (state) => {
      state.selectedCourse = null;
    },
    setOrderFilters: (state, action) => {
      state.orderFilters = { ...state.orderFilters, ...action.payload };
    },
    clearSelectedOrder: (state) => {
      state.selectedOrder = null;
    },
    setPromoCodeFilters: (state, action) => {
      state.promoCodeFilters = { ...state.promoCodeFilters, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    // Dashboard Stats
    builder
      .addCase(getDashboardStats.pending, (state) => {
        state.statsLoading = true;
        state.error = null;
      })
      .addCase(getDashboardStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload.stats;
      })
      .addCase(getDashboardStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.error = action.payload;
      });

    // Recent Activities
    builder
      .addCase(getRecentActivities.pending, (state) => {
        state.activitiesLoading = true;
        state.error = null;
      })
      .addCase(getRecentActivities.fulfilled, (state, action) => {
        state.activitiesLoading = false;
        state.activities = action.payload.activities;
      })
      .addCase(getRecentActivities.rejected, (state, action) => {
        state.activitiesLoading = false;
        state.error = action.payload;
      });

    // Sales Chart Data
    builder
      .addCase(getSalesChartData.pending, (state) => {
        state.salesChartDataLoading = true;
        state.error = null;
      })
      .addCase(getSalesChartData.fulfilled, (state, action) => {
        state.salesChartDataLoading = false;
        state.salesChartData = action.payload.chartData;
      })
      .addCase(getSalesChartData.rejected, (state, action) => {
        state.salesChartDataLoading = false;
        state.error = action.payload;
      });

    // Course Performance
    builder
      .addCase(getCoursePerformance.pending, (state) => {
        state.coursePerformanceLoading = true;
        state.error = null;
      })
      .addCase(getCoursePerformance.fulfilled, (state, action) => {
        state.coursePerformanceLoading = false;
        state.coursePerformance = action.payload.courses;
      })
      .addCase(getCoursePerformance.rejected, (state, action) => {
        state.coursePerformanceLoading = false;
        state.error = action.payload;
      });

    // Revenue Analytics
    builder
      .addCase(getRevenueAnalytics.pending, (state) => {
        state.revenueAnalyticsLoading = true;
        state.error = null;
      })
      .addCase(getRevenueAnalytics.fulfilled, (state, action) => {
        state.revenueAnalyticsLoading = false;
        state.revenueAnalytics = action.payload;
      })
      .addCase(getRevenueAnalytics.rejected, (state, action) => {
        state.revenueAnalyticsLoading = false;
        state.error = action.payload;
      });

    // Get All Users
    builder
      .addCase(getAllUsers.pending, (state) => {
        state.usersLoading = true;
        state.error = null;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.users = action.payload.users;
        state.usersPagination = action.payload.pagination;
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.error = action.payload;
      });

    // Get User By ID
    builder
      .addCase(getUserById.pending, (state) => {
        state.userLoading = true;
        state.error = null;
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.userLoading = false;
        state.selectedUser = action.payload.user;
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.userLoading = false;
        state.error = action.payload;
      });

    // Update User
    builder
      .addCase(updateUser.pending, (state) => {
        state.userUpdateLoading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.userUpdateLoading = false;
        // Update user in the users array
        const index = state.users.findIndex(
          (user) => user.id === action.payload.user.id
        );
        if (index !== -1) {
          state.users[index] = action.payload.user;
        }
        // Update selectedUser if it's the same user
        if (
          state.selectedUser &&
          state.selectedUser.id === action.payload.user.id
        ) {
          state.selectedUser = action.payload.user;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.userUpdateLoading = false;
        state.error = action.payload;
      });

    // Delete User
    builder
      .addCase(deleteUser.pending, (state) => {
        state.userDeleteLoading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.userDeleteLoading = false;
        // Don't modify users array here - let the subsequent getAllUsers call handle it
        // Clear selectedUser if it's the deleted user
        if (
          state.selectedUser &&
          state.selectedUser.id === action.payload.userId
        ) {
          state.selectedUser = null;
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.userDeleteLoading = false;
        state.error = action.payload;
      });

    // Get All Courses
    builder
      .addCase(getAllCourses.pending, (state) => {
        state.coursesLoading = true;
        state.error = null;
      })
      .addCase(getAllCourses.fulfilled, (state, action) => {
        state.coursesLoading = false;
        state.courses = action.payload.courses;
        state.coursesPagination = action.payload.pagination;
        state.coursesTotals = action.payload.totals || null;
      })
      .addCase(getAllCourses.rejected, (state, action) => {
        state.coursesLoading = false;
        state.error = action.payload;
      });

    // Get Course By ID
    builder
      .addCase(getCourseById.pending, (state) => {
        state.courseLoading = true;
        state.error = null;
      })
      .addCase(getCourseById.fulfilled, (state, action) => {
        state.courseLoading = false;
        state.selectedCourse = action.payload.course;
      })
      .addCase(getCourseById.rejected, (state, action) => {
        state.courseLoading = false;
        state.error = action.payload;
      });

    // Create Course
    builder
      .addCase(createCourse.pending, (state) => {
        state.courseCreateLoading = true;
        state.error = null;
      })
      .addCase(createCourse.fulfilled, (state, action) => {
        state.courseCreateLoading = false;
        state.courses.unshift(action.payload.course);
      })
      .addCase(createCourse.rejected, (state, action) => {
        state.courseCreateLoading = false;
        state.error = action.payload;
      });

    // Update Course
    builder
      .addCase(updateCourse.pending, (state) => {
        state.courseUpdateLoading = true;
        state.error = null;
      })
      .addCase(updateCourse.fulfilled, (state, action) => {
        state.courseUpdateLoading = false;
        const index = state.courses.findIndex(
          (course) => course.id === action.payload.course.id
        );
        if (index !== -1) {
          state.courses[index] = action.payload.course;
        }
        if (
          state.selectedCourse &&
          state.selectedCourse.id === action.payload.course.id
        ) {
          state.selectedCourse = action.payload.course;
        }
      })
      .addCase(updateCourse.rejected, (state, action) => {
        state.courseUpdateLoading = false;
        state.error = action.payload;
      });

    // Delete Course
    builder
      .addCase(deleteCourse.pending, (state) => {
        state.courseDeleteLoading = true;
        state.error = null;
      })
      .addCase(deleteCourse.fulfilled, (state, action) => {
        state.courseDeleteLoading = false;
        if (
          state.selectedCourse &&
          state.selectedCourse.id === action.payload.courseId
        ) {
          state.selectedCourse = null;
        }
      })
      .addCase(deleteCourse.rejected, (state, action) => {
        state.courseDeleteLoading = false;
        state.error = action.payload;
      });

    // Orders
    builder
      .addCase(getAllOrders.pending, (state) => {
        state.ordersLoading = true;
        state.error = null;
      })
      .addCase(getAllOrders.fulfilled, (state, action) => {
        state.ordersLoading = false;
        state.orders = action.payload.orders;
        state.ordersPagination = action.payload.pagination;
      })
      .addCase(getAllOrders.rejected, (state, action) => {
        state.ordersLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(getOrderById.pending, (state) => {
        state.orderLoading = true;
        state.error = null;
      })
      .addCase(getOrderById.fulfilled, (state, action) => {
        state.orderLoading = false;
        state.selectedOrder = action.payload.order;
      })
      .addCase(getOrderById.rejected, (state, action) => {
        state.orderLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(updateOrderStatus.pending, (state) => {
        state.orderUpdateLoading = true;
        state.error = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.orderUpdateLoading = false;
        const updatedOrder = action.payload.order;
        const index = state.orders.findIndex(
          (order) => order.id === updatedOrder.id
        );
        if (index !== -1) {
          state.orders[index] = updatedOrder;
        }
        if (state.selectedOrder && state.selectedOrder.id === updatedOrder.id) {
          state.selectedOrder = updatedOrder;
        }
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.orderUpdateLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(deleteOrder.pending, (state) => {
        state.orderDeleteLoading = true;
        state.error = null;
      })
      .addCase(deleteOrder.fulfilled, (state, action) => {
        state.orderDeleteLoading = false;
        state.orders = state.orders.filter(
          (order) => order.id !== action.payload.orderId
        );
        if (
          state.selectedOrder &&
          state.selectedOrder.id === action.payload.orderId
        ) {
          state.selectedOrder = null;
        }
      })
      .addCase(deleteOrder.rejected, (state, action) => {
        state.orderDeleteLoading = false;
        state.error = action.payload;
      });

    // Promo Codes
    builder
      .addCase(getAllPromoCodes.pending, (state) => {
        state.promoCodesLoading = true;
        state.error = null;
      })
      .addCase(getAllPromoCodes.fulfilled, (state, action) => {
        state.promoCodesLoading = false;
        state.promoCodes = action.payload.promoCodes;
        state.promoCodesPagination = action.payload.pagination;
      })
      .addCase(getAllPromoCodes.rejected, (state, action) => {
        state.promoCodesLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(createPromoCode.pending, (state) => {
        state.promoCodeCreateLoading = true;
        state.error = null;
      })
      .addCase(createPromoCode.fulfilled, (state, action) => {
        state.promoCodeCreateLoading = false;
        state.promoCodes.unshift(action.payload.promoCode);
      })
      .addCase(createPromoCode.rejected, (state, action) => {
        state.promoCodeCreateLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(deletePromoCode.pending, (state) => {
        state.promoCodeDeleteLoading = true;
        state.error = null;
      })
      .addCase(deletePromoCode.fulfilled, (state, action) => {
        state.promoCodeDeleteLoading = false;
        state.promoCodes = state.promoCodes.filter(
          (pc) => pc.id !== action.payload.promoId
        );
      })
      .addCase(deletePromoCode.rejected, (state, action) => {
        state.promoCodeDeleteLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setTimeframe,
  setPeriod,
  setUserFilters,
  clearSelectedUser,
  setCourseFilters,
  clearSelectedCourse,
  setOrderFilters,
  clearSelectedOrder,
  setPromoCodeFilters,
} = adminSlice.actions;

// Selectors
export const selectStats = (state) => state.admin.stats;
export const selectActivities = (state) => state.admin.activities;
export const selectSalesChartData = (state) => state.admin.salesChartData;
export const selectCoursePerformance = (state) => state.admin.coursePerformance;
export const selectRevenueAnalytics = (state) => state.admin.revenueAnalytics;

// User selectors
export const selectUsers = (state) => state.admin.users;
export const selectSelectedUser = (state) => state.admin.selectedUser;
export const selectUsersPagination = (state) => state.admin.usersPagination;
export const selectUserFilters = (state) => state.admin.userFilters;

// Course selectors
export const selectCourses = (state) => state.admin.courses;
export const selectSelectedCourse = (state) => state.admin.selectedCourse;
export const selectCoursesPagination = (state) => state.admin.coursesPagination;
export const selectCoursesTotals = (state) => state.admin.coursesTotals;
export const selectCourseFilters = (state) => state.admin.courseFilters;

// Order selectors
export const selectOrders = (state) => state.admin.orders;
export const selectSelectedOrder = (state) => state.admin.selectedOrder;
export const selectOrdersPagination = (state) => state.admin.ordersPagination;
export const selectOrderFilters = (state) => state.admin.orderFilters;

// Loading selectors
export const selectStatsLoading = (state) => state.admin.statsLoading;
export const selectActivitiesLoading = (state) => state.admin.activitiesLoading;
export const selectSalesChartDataLoading = (state) => state.admin.salesChartDataLoading;
export const selectCoursePerformanceLoading = (state) =>
  state.admin.coursePerformanceLoading;
export const selectRevenueAnalyticsLoading = (state) =>
  state.admin.revenueAnalyticsLoading;
export const selectUsersLoading = (state) => state.admin.usersLoading;
export const selectUserLoading = (state) => state.admin.userLoading;
export const selectUserUpdateLoading = (state) => state.admin.userUpdateLoading;
export const selectUserDeleteLoading = (state) => state.admin.userDeleteLoading;
export const selectCoursesLoading = (state) => state.admin.coursesLoading;
export const selectCourseLoading = (state) => state.admin.courseLoading;
export const selectCourseCreateLoading = (state) =>
  state.admin.courseCreateLoading;
export const selectCourseUpdateLoading = (state) =>
  state.admin.courseUpdateLoading;
export const selectCourseDeleteLoading = (state) =>
  state.admin.courseDeleteLoading;
export const selectOrdersLoading = (state) => state.admin.ordersLoading;
export const selectOrderLoading = (state) => state.admin.orderLoading;
export const selectOrderUpdateLoading = (state) =>
  state.admin.orderUpdateLoading;
export const selectOrderDeleteLoading = (state) =>
  state.admin.orderDeleteLoading;

// Promo code selectors
export const selectPromoCodes = (state) => state.admin.promoCodes;
export const selectPromoCodesPagination = (state) => state.admin.promoCodesPagination;
export const selectPromoCodesLoading = (state) => state.admin.promoCodesLoading;
export const selectPromoCodeCreateLoading = (state) => state.admin.promoCodeCreateLoading;
export const selectPromoCodeDeleteLoading = (state) => state.admin.promoCodeDeleteLoading;
export const selectPromoCodeFilters = (state) => state.admin.promoCodeFilters;

export const selectError = (state) => state.admin.error;
export const selectSelectedTimeframe = (state) => state.admin.selectedTimeframe;
export const selectSelectedPeriod = (state) => state.admin.selectedPeriod;

export default adminSlice.reducer;
